import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { TemplatePtAPI } from "@repo/api";
import { UserAPI } from "@repo/api/user";
import { useAuth } from "@repo/context/auth/auth";
import { templateKeys, type TemplateListType } from "@repo/query";

type ErrorLike = { msg?: string } | null | undefined;

/** 三个 Tab 对应三种接口参数组合 */
function fetchTemplatesByType(
  type: TemplateListType,
  page: number,
  pageSize: number,
  search: string,
) {
  if (type === "received") {
    return TemplatePtAPI.getReceivedTemplates(page, pageSize, true, search);
  }
  if (type === "uploaded") {
    return TemplatePtAPI.getTemplateList(page, pageSize, search);
  }
  return TemplatePtAPI.getReceivedTemplates(page, pageSize, false, search);
}

export interface TemplateInfiniteListParams {
  type: TemplateListType;
  pageSize: number;
  search: string;
  enabled: boolean;
}

/**
 * 无限滚动的模板列表（H5）。切 Tab / 搜索词变化会换 key，旧请求结果不会串到新列表里。
 * 返回的页数少于 pageSize 即视为到底。
 */
export function useTemplateInfiniteList({
  type,
  pageSize,
  search,
  enabled,
}: TemplateInfiniteListParams) {
  return useInfiniteQuery({
    queryKey: templateKeys.list({ type, search, pageSize }),
    queryFn: ({ pageParam }) =>
      fetchTemplatesByType(type, pageParam, pageSize, search).then((r) => r.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage.content.length === pageSize ? pages.length + 1 : undefined,
    enabled,
  });
}

/** 底部导航「待签收」角标，只取 total */
export function usePendingTemplateCount(enabled: boolean) {
  return useQuery({
    queryKey: templateKeys.pendingCount(),
    queryFn: () => TemplatePtAPI.getReceivedTemplates(1, 1, false).then((r) => r.data.total),
    enabled,
  });
}

/** 绑定收货 QQ。成功后刷新登录用户信息即可，不需要整页 reload。 */
export function useBindQQ() {
  const { refreshUserInfo } = useAuth();
  return useMutation({
    mutationFn: (qq: string) => UserAPI.updateUserInfo({ qq }),
    onSuccess: async () => {
      toast.success("QQ号码绑定成功");
      await refreshUserInfo();
    },
    onError: (error: ErrorLike) => {
      toast.error(error?.msg || "QQ号码绑定失败");
    },
  });
}
